import { api } from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import type { ApiResponse, Product, ProductListResponse, ProductCustomInfoResponse, ProductCustomInfoEntry, CreateCustomInfoEntryInput, ProductBlockStatus, ProductSyncConfig, ProductSyncStatus } from '@/types';

type BuScope = { bu_id?: string };

/**
 * The product catalogue is per BRAND, because a Shopify store is: an account can hold one
 * store per brand, so two brands own two disjoint catalogues. Every call carries the brand
 * the operator is working as — reads as a `bu_id` param, writes appended to the URL, the
 * same split the rest of the services use.
 *
 * An ABSENT scope means account-wide, which is what a single-brand account always sends.
 */
export const productService = {
  async listProducts(params: {
    query?: string;
    first?: number;
    after?: string;
    status?: string;
  }, scope?: BuScope): Promise<ProductListResponse> {
    const searchParams = new URLSearchParams();
    if (params.query) searchParams.set('query', params.query);
    if (params.first) searchParams.set('first', String(params.first));
    if (params.after) searchParams.set('after', params.after);
    if (params.status) searchParams.set('status', params.status);
    if (scope?.bu_id) searchParams.set('bu_id', scope.bu_id);

    const query = searchParams.toString();
    const url = `${API_ENDPOINTS.PRODUCTS.BASE}${query ? `?${query}` : ''}`;

    const response = await api.get<ApiResponse<ProductListResponse>>(url);
    return response.data.data;
  },

  async getProduct(productId: string, scope?: BuScope): Promise<Product> {
    const response = await api.get<ApiResponse<Product>>(
      API_ENDPOINTS.PRODUCTS.BY_ID(productId),
      { params: scope },
    );
    return response.data.data;
  },

  async listCustomInfo(productId: string, scope?: BuScope): Promise<ProductCustomInfoResponse> {
    const response = await api.get<ApiResponse<ProductCustomInfoResponse>>(
      API_ENDPOINTS.PRODUCTS.CUSTOM_INFO(productId),
      { params: scope },
    );
    return response.data.data;
  },

  async createCustomInfo(
    productId: string,
    data: CreateCustomInfoEntryInput,
    scope?: BuScope,
  ): Promise<{ entry: ProductCustomInfoEntry }> {
    const response = await api.post<ApiResponse<{ entry: ProductCustomInfoEntry }>>(
      withBuScope(API_ENDPOINTS.PRODUCTS.CUSTOM_INFO(productId), scope ?? {}),
      data,
    );
    return response.data.data;
  },

  async updateCustomInfo(
    productId: string,
    entryId: string,
    data: Partial<CreateCustomInfoEntryInput>,
    scope?: BuScope,
  ): Promise<{ entry: ProductCustomInfoEntry }> {
    const response = await api.patch<ApiResponse<{ entry: ProductCustomInfoEntry }>>(
      withBuScope(API_ENDPOINTS.PRODUCTS.CUSTOM_INFO_ENTRY(productId, entryId), scope ?? {}),
      data,
    );
    return response.data.data;
  },

  async archiveCustomInfo(productId: string, entryId: string, scope?: BuScope): Promise<void> {
    await api.delete(withBuScope(API_ENDPOINTS.PRODUCTS.CUSTOM_INFO_ENTRY(productId, entryId), scope ?? {}));
  },

  async getBlockStatus(productId: string, scope?: BuScope): Promise<ProductBlockStatus> {
    const response = await api.get<ApiResponse<ProductBlockStatus>>(
      `${API_ENDPOINTS.PRODUCTS.BY_ID(productId)}/block`,
      { params: scope },
    );
    return response.data.data;
  },

  async toggleBlock(productId: string, blocked: boolean, scope?: BuScope): Promise<ProductBlockStatus> {
    const response = await api.patch<ApiResponse<ProductBlockStatus>>(
      withBuScope(`${API_ENDPOINTS.PRODUCTS.BY_ID(productId)}/block`, scope ?? {}),
      { blocked },
    );
    return response.data.data;
  },

  /** Kicks off a Shopify -> Qdrant catalog sync for the brand's store. Returns a jobId to poll. */
  async startSync(scope?: BuScope): Promise<{ jobId?: string }> {
    const response = await api.post<ApiResponse<{ jobId?: string }>>(
      withBuScope(API_ENDPOINTS.PRODUCTS.SYNC, scope ?? {}),
    );
    return response.data.data;
  },

  async getSyncStatus(jobId: string, scope?: BuScope): Promise<ProductSyncStatus> {
    const response = await api.get<ApiResponse<ProductSyncStatus>>(
      API_ENDPOINTS.PRODUCTS.SYNC_STATUS(jobId),
      { params: scope },
    );
    return response.data.data;
  },

  async getSyncConfig(scope?: BuScope): Promise<ProductSyncConfig> {
    const response = await api.get<ApiResponse<ProductSyncConfig>>(
      API_ENDPOINTS.PRODUCTS.SYNC_CONFIG,
      { params: scope },
    );
    return response.data.data;
  },

  async updateSyncConfig(enabledFields: string[], scope?: BuScope): Promise<{ enabledFields: string[] }> {
    const response = await api.patch<ApiResponse<{ enabledFields: string[] }>>(
      withBuScope(API_ENDPOINTS.PRODUCTS.SYNC_CONFIG, scope ?? {}),
      { enabledFields },
    );
    return response.data.data;
  },
};
